import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { Helmet } from "react-helmet";
import AdminLayout from "../../layout/AdminLayout";
import { useAuth } from "../../../context/AuthContext";
import * as model from "../../../models/UserModel";
import { useNotificationCount } from "../../../context/NotificationCountContext";
import UserTable from "./UserTable";
import { ConfirmDelete } from "../../modals/ConfirmDelete";
import { apiError } from "../../../helpers/ErrorHelper";
import { toast } from "react-toastify";
import { UserForm } from "./UserForm";
import PageHeader from "../../common/PageHeader";
import config from "../../../config/config";
import { checkPermission, isSuperAdminUser } from "../../../helpers/GeneralHelper";
import { useLoginActivityAccess } from "../../../hooks/useLoginActivityAccess";
import { UserView } from "./UserView";
import { useDebouncedCallback } from 'use-debounce';
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

export default function Users() {
  const module = {
    title: 'Team Members',
    singularTitle: 'Member',
    key: 'users',
    route: 'users',
    primaryCol: '_user_id'
  };

  const { state } = useAuth();
  let user_data = state.user_data;
  const { canView: canViewSessionLogs, access: loginActivityAccess } =
    useLoginActivityAccess();
  const sessionLogsManagerId =
    loginActivityAccess?.scope === "team" ? user_data?._user_id : null;

  const navigate = useNavigate();

  const { notificationCount, setNotificationCount } = useNotificationCount();

  const [modalKey, setModalKey] = useState(0);

  const [toggleAddModal, setToggleAddModal] = useState(false);

  // Update Start
  const [toggleUpdateModal, setToggleUpdateModal] = useState(false);
  const [singleData, setSingleData] = useState({});
  const openUpdateModal = (data) => {
    setSingleData(data);
    setModalKey((prevKey) => prevKey + 1);
    setToggleUpdateModal(true);
  };
  // Update End

  const [toggleViewModal, setToggleViewModal] = useState(false);
  const handleView = (data) => {
    setSingleData(data);
    setToggleViewModal(true);
  }

  // Delete Start
  const [toggleDeleteModal, setToggleDeleteModal] = useState(false);
  const openDeleteModal = (data) => {
    setSingleData(data);
    setToggleDeleteModal(true);
  };

  const deleteData = async() => {
    setLoading(true);

    try {
      const response = await model.remove(user_data.user_token, singleData.user_unique_id);
      if (response.status) {
        setNotificationCount(localStorage.getItem('unread_notification_count'));
        toast.success(response.message);

        model.saveToStorage(response.data[module.key], response.data.pagination);
        refreshData(); 
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      apiError(error);

      if (error.response?.data.invalid_token == 1) {
        navigate('/re-login');
      }
    }

    setToggleDeleteModal(false);
    setLoading(false);
  }
  // Delete End

  // Flush Start
  const flashData = async () => {
    localStorage.removeItem(module.key);
    localStorage.removeItem('designations_master');
    localStorage.removeItem('managers');
    localStorage.removeItem('user_roles');

    fetchData(limit, page);
  };
  // Flush End

  // List Start
  const [listData, setListData] = useState([]);
  const [cacheTime, setCacheTime] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState("active");
  const [isTrash, setIsTrash] = useState(false);

  const fetchTrash = (isTrash) => {
    setIsTrash(isTrash);
  }

  const debounced = useDebouncedCallback(() => fetchData(limit, page), 500);

  const fetchData = async (newLimit, page) => {
    setLoading(true);

    try {
      let apiParam = {
        search: searchQuery,
        page: page,
        limit: newLimit,
        user_token: user_data.user_token,
        isTrash: isTrash ? 1 : 0,
      };
      if (statusFilter === "inactive") {
        apiParam.user_is_active = 0;
      } else {
        apiParam.user_is_active = 1;
      }
      const response = await model.getData(apiParam);
      if (typeof response == typeof undefined) {
        navigate("/re-login");
        return;
      }

      setListData(response[module.key]);
      setTotalRows(response.pagination.total_rows);
      setLimit(newLimit);
      setPage(page);

      setNotificationCount(localStorage.getItem("unread_notification_count"));

      if (response.time) {
        setCacheTime(response.time);
      }

      setLoading(false);
    } catch (error) {
      console.error(error);
    }
  };
  // List End

  const refreshData = async () => {
    fetchData(limit, page);
  }

  const exportToExcel = async () => {
      if (!listData || listData.length === 0) {
        toast.warning("No data to export");
        return;
      }
  
      // Generate timestamp (YYYYMMDD_HHMMSS + random 4 digits)
      const timestamp = new Date().toISOString()
                                  .replace(/[-:T]/g, "") // remove special chars
                                  .split(".")[0];        // remove milliseconds
  
      const fileName = `Team_Members_${timestamp}.xlsx`;
  
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Team Members");
  
      // Define headers and style
      const headers = [
        { header: "Name", key: "user_name" },
        { header: "Mobile", key: "user_mobile" },
        { header: "Email", key: "user_email" },
        { header: "Role", key: "urole_title" },
        { header: "Designation", key: "designation_name" },
        { header: "Manager", key: "manager_name" },
        { header: "Status", key: "status_label" },
      ];
  
      //worksheet.columns = headers;
      worksheet.columns = [
        { header: "Name", key: "user_name" },
        { header: "Mobile", key: "user_mobile" },
        { header: "Email", key: "user_email" },
        { header: "Role", key: "urole_title" },
        { header: "Designation", key: "designation_name" },
        { header: "Manager", key: "manager_name" },
        { header: "Status", key: "status_label" },
      ];
  
      // Add rows
      listData.forEach(row => {
        const active =
          row.user_is_active == 1 ||
          row.user_is_active === "1" ||
          row.user_is_active === true ||
          String(row.user_is_active || "").toLowerCase() === "active";
        worksheet.addRow({
          user_name: row.user_name,
          user_mobile: row.user_mobile || "N/A",
          user_email: row.user_email || "N/A",
          urole_title: row.urole_title || "N/A",
          designation_name: row.designation_name || "N/A",
          manager_name: row.manager_name || "N/A",
          status_label: active ? "Active" : "Inactive",
        });
      });
  
      // Style header
      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'D9E1F2' }, // Light blue
        };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
      });
  
      // Generate Excel file and save
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, fileName);
    };

  return (
    <AdminLayout>
      <Helmet>
        <title>{module.title} - Digital CMS</title>
      </Helmet>

      <div className="main-content app-content">
        <div className="container-fluid">
          <PageHeader module={module} isTrash={isTrash} toggleTrash={fetchTrash} />

          {/* <!-- Start::row-1 --> */}
          <div className="row">
            <div className="col-xl-12">
              <div className="card custom-card">
                {! isTrash && <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-3">
                  <div className="card-title">{ config.isCache ? `Last Synced On ${cacheTime}` : module.title }</div>

                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      {listData.length > 0 && (
                        <button
                          type="button"
                          className="btn btn-primary d-inline-flex align-items-center"
                          onClick={exportToExcel}
                        >
                          <i className="ri-file-excel-2-line me-2 align-middle fs-16"></i>
                          Download Excel
                        </button>
                      )}
                      <div
                        className="input-group shadow-sm border border-primary border-opacity-25 rounded-3 overflow-hidden"
                        style={{ width: "auto", minWidth: 220, maxWidth: 280 }}
                      >
                        <span
                          className="input-group-text border-0 bg-primary-transparent text-primary fw-semibold px-3"
                          id="user-status-filter-label"
                        >
                          <i className="ri-equalizer-line me-2 align-middle"></i>
                          Status
                        </span>
                        <select
                          className="form-select border-0 rounded-0"
                          aria-labelledby="user-status-filter-label"
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                      <input
                        type="text"
                        className="form-control table-search"
                        onChange={(e) => {
                          debounced(e.target.value);
                          setSearchQuery(e.target.value);
                        }}
                        placeholder="Search..."
                        autoComplete="off"
                      />

                      {checkPermission(module.route, 'add') && (
                        <button className="btn btn-primary btn-sm" onClick={() => setToggleAddModal(true)}>
                          <i className="ri-add-line me-1 fw-semibold align-middle"></i>
                          Add
                        </button>
                      )}
                    </div>

                    {config.isCache ? 
                      <button className="btn btn-success-light btn-sm" onClick={() => flashData()}>
                        <i className="ri-refresh-line me-1 fw-semibold align-middle"></i> Refresh
                      </button> : ''
                    }
                  </div>
                </div>}
                <div className="card-body">
                  <div className="table-responsive">
                    <UserTable
                      module={module}
                      data={listData}
                      loading={loading}
                      totalRows={totalRows}
                      page={page}
                      limit={limit}
                      fetchData={fetchData}
                      openUpdateModal={openUpdateModal}
                      openDeleteModal={openDeleteModal}
                      handleView={handleView}
                      isTrash={isTrash}
                      statusFilter={statusFilter}
                      canViewSessionLogs={canViewSessionLogs}
                      sessionLogsManagerId={sessionLogsManagerId}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* <!--End::row-1 --> */}

          {/* <!--Add Modal Start--> */}
          <UserForm
            module={module}
            showModal={toggleAddModal}
            setShowModal={setToggleAddModal}
            refreshData={refreshData}
          />
          {/* <!--Add Modal End--> */}

          {/* <!--Update Modal Start--> */}
          <UserForm
            module={module}
            showModal={toggleUpdateModal}
            setShowModal={setToggleUpdateModal}
            refreshData={refreshData}
            data={singleData}
            modalKey={modalKey}
          />
          {/* <!--Update Modal End--> */}

          {/* <!--Delete Modal Start--> */}
          <ConfirmDelete
            module={module}
            showModal={toggleDeleteModal}
            setShowModal={setToggleDeleteModal}
            loading={loading}
            onConfirm={deleteData}
          />
          {/* <!--Delete Modal End--> */}

          {/* View Modal */}
          <UserView
            module={module}
            showModal={toggleViewModal}
            setShowModal={setToggleViewModal}
            data={singleData}
          />
        </div>
      </div>

      <Helmet></Helmet>
    </AdminLayout>
  );
}
